<template>
    <div id="main">
        <container-loader v-if="isLoading" />
        <div class="page page-container">
            <alert-container :messages="msgs" />

            <div class="vertical-flow">
                <div class="listing-header">
                    <!-- HEADER -->
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t('rentabilidade')
                            }}{{
                                rentabilidade.id != null
                                    ? `: ${rentabilidade.id}`
                                    : `: ${$t('nova-rentabilidade')}`
                            }}
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{ $t('vendas') }}</a>
                                </li>
                                <li>
                                    <a
                                        @click="
                                            $router.push({
                                                name: 'ven-rentabilidade-list',
                                            })
                                        "
                                        >{{ $t('rentabilidade') }}</a
                                    >
                                </li>
                                <li>
                                    <a aria-current="page">{{
                                        rentabilidade.id != null
                                            ? rentabilidade.id
                                            : $t('nova-rentabilidade')
                                    }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    <!-- BOTÕES DE AÇÂO -->
                    <div class="header-buttons">
                        <button
                            class="delete-btn"
                            type="button"
                            tag="button"
                            @click="promptChangeTab"
                        >
                            {{ $t('voltar') }}
                        </button>
                        <button
                            v-if="
                                (!usuarioIntegracao &&
                                    !isUpdate &&
                                    permissao.create) ||
                                (!usuarioIntegracao &&
                                    isUpdate &&
                                    permissao.update)
                            "
                            class="save-btn"
                            type="button"
                            @click="save"
                            :disabled="isSaving"
                        >
                            {{ $t('salvar') }}
                            <button-loader :isSaving="isSaving" />
                        </button>
                    </div>
                </div>
                <br />

                <!-- FORM -->
                <div class="form-columns-fit">
                    <div class="columns is-flex-wrap">
                        <div class="column is-2">
                            <v-input
                                :id="generateId(6)"
                                ref="idErpInput"
                                v-model="rentabilidade.idrentabilidadeerp"
                                placeholder="placeholder-preencha"
                                :maxlength="30"
                                title="codigo"
                                uppercase
                                required
                                :invalid="!rentabilidade.idrentabilidadeerp"
                                tooltipLabel="cod-ref"
                                :disabled="cantEdit"
                            />
                        </div>
                        <div class="column is-5">
                            <vss-select
                                :id="generateId(1)"
                                ref="empresasSelect"
                                v-model="empresaSelected"
                                title="empresa"
                                :required="true"
                                :options="empresaList"
                                :searchable="false"
                                class="no-clear"
                                tooltipLabel="informacao-rentabilidade-empresa"
                                tooltipPosition="is-right"
                                :disabled="cantEdit"
                            />
                        </div>
                        <div class="column is-5">
                            <v-input
                                :id="generateId(2)"
                                ref="tipoInput"
                                v-model="rentabilidade.tipo"
                                title="tipo"
                                :required="true"
                                placeholder="placeholder-preencha"
                                :maxlength="50"
                                tooltipLabel="informacao-rentabilidade-tipo"
                                tooltipPosition="is-top"
                                msg="msg-campo-obrigatorio"
                                :invalid="!rentabilidade.tipo"
                                uppercase
                                :disabled="cantEdit"
                            />
                        </div>
                        <div class="column is-4">
                            <label for="color">
                                {{ $t('selecione-uma-cor') }}
                                <b-icon
                                    icon="information"
                                    size="is-small"
                                    v-tippy
                                    :title="
                                        $t('informacao-rentabilidade-color')
                                    "
                                />
                            </label>
                            <color-picker
                                :id="generateId(3)"
                                ref="colorInput"
                                :color="rentabilidade.cor"
                                type="text"
                                @colorpicked="colorpicked"
                                :disabled="cantEdit"
                            />
                        </div>
                        <div class="column is-4">
                            <!-- -Infinity foi utilizado como parametro para permitir qualquer numero negativo -->
                            <v-money
                                :id="generateId(4)"
                                ref="percentualInput"
                                v-model="rentabilidade.percentual"
                                title="percentual"
                                placeholder="placeholder-preencha"
                                suffix="%"
                                :tooltipMultilined="true"
                                tooltipPosition="is-top"
                                tooltipLabel="informacao-rentabilidade-percentual"
                                tooltipSize="is-small"
                                :disabled="cantEdit"
                                :invalid="!rentabilidade.percentual"
                                required
                                msg="msg-campo-obrigatorio"
                                :min="-Infinity"
                            />
                        </div>
                        <div class="column is-4">
                            <vss-select
                                :id="generateId(5)"
                                ref="ed"
                                v-model="rentabilidade.situacao"
                                title="situacao"
                                :required="true"
                                :options="situacoes"
                                :searchable="false"
                                class="no-clear"
                                :disabled="cantEdit || isCreate"
                                tooltipLabel="info-situacao-generica"
                                mlLabels
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import RentabilidadeService from '@/services/vendas/RentabilidadeService';
import RentabilidadeValidation from '@/services/validations/vendas/RentabilidadeValidation';
import EmpresaService from '@/services/vendas/empresa/EmpresaService';
import ContainerLoader from '@/components/content/loader/ContainerLoader';
import ColorPicker from '@/components/shared/form/ColorPicker';
import Message from '@/models/Message';

export default {
    components: {
        ColorPicker,
        ContainerLoader,
    },
    data() {
        return {
            msgs: [],
            service: new RentabilidadeService(this.$http, this.$router),
            empresaService: new EmpresaService(this.$http, this.$router),
            validation: new RentabilidadeValidation(),
            usuarioIntegracao:
                this.$store.state.user.tabelasERPWebServer.includes(
                    'RENTABILIDADE'
                ),
            permissao: {},
            situacoes: [
                { label: 'ativo', id: 1 },
                { label: 'inativo', id: 0 },
            ],
            rentabilidade: {
                id: null,
                idrentabilidadeerp: null,
                cor: '#D0CFCF',
                situacao: 1,
                tipo: null,
                percentual: '',
                idempresa: null,
            },
            empresaList: [{ id: 0, label: this.$t('todos') }],
            empresaSelected: { id: 0, label: this.$t('todos') },
            isUpdate: false,
            isCreate: false,
            isChanged: false,
            isSaving: false,
            isLoading: false,
            cantEdit: false,
            finishedLoad: false,
        };
    },
    watch: {
        // Watcher que checa se a rentabilidade foi alterada depois de ter sido carregada
        rentabilidade: {
            deep: true,
            handler: function () {
                if (this.finishedLoad) {
                    this.isChanged = true;
                }
            },
        },
    },
    created() {
        if (this.$route.params.id) {
            this.isUpdate = true;
        } else {
            this.isCreate = true;
        }

        this.$store.state.empresas.data.map((e) => {
            this.empresaList.push({ id: e.id, label: e.nome });
        });

        this.permissao = this.hasPermissionAccess(
            'vendas',
            'vendas',
            'ven-rentabilidade'
        );

        if (
            (!this.isUpdate && !this.permissao.create) ||
            (this.isUpdate && !this.permissao.read)
        ) {
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'rentabilidade' },
            });
        } else {
            if (this.isUpdate) {
                this.cantEdit =
                    this.usuarioIntegracao || !this.permissao.update;
                this.carregarDados();
            }
        }
    },
    methods: {
        // Gera ids para teste
        generateId(numberId) {
            return this.$route.name + '-' + numberId;
        },
        carregarDados() {
            this.isLoading = true;
            this.service
                .find(this.$route.params.id)
                .then((value) => {
                    this.rentabilidade = value.content;

                    this.rentabilidade.percentual = this.isNull(this.rentabilidade.percentual)
                        ? ''
                        : this.rentabilidade.percentual;
                    this.rentabilidade.cor = this.rentabilidade.cor
                        ? this.rentabilidade.cor
                        : '#D0CFCF';
                    
                    this.rentabilidade.situacao = this.situacoes.find(({id}) => id === this.rentabilidade.situacao);
                    
                    this.rentabilidade.tipo = this.rentabilidade.tipo
                        ? this.rentabilidade.tipo
                        : '';
                    this.rentabilidade.idempresa = this.rentabilidade.empresa
                        ? this.rentabilidade.empresa.id
                        : null;
                    this.rentabilidade.idrentabilidadeerp = this.rentabilidade.idrentabilidadeerp
                        ? this.rentabilidade.idrentabilidadeerp
                        : '';
                    if (value.content.empresa.id) {
                        this.empresaSelected.label = value.content.empresa.nome;
                        this.empresaSelected.id = value.content.empresa.id;
                    }
                })
                .catch((e) => {
                    this.rentabilidade = {};
                    this.msgs.push(new Message('error', 'msg-erro-load', true));
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.finishedLoad = true;
                });
        },
        save() {
            this.isLoading = true;

            if (this.validation.validate(this.rentabilidade, this.$refs)) {
                let rentabilidadeTemp = Object.assign({}, this.rentabilidade);
                rentabilidadeTemp = this.mapTosave(rentabilidadeTemp);
                
                rentabilidadeTemp.situacao = this.rentabilidade.situacao.id;
                
                rentabilidadeTemp.idempresa = this.empresaSelected.id;

                this.isSaving = true;
                this.service
                    .save(rentabilidadeTemp)
                    .then((r) => {
                        let msg = this.$t(
                                this.rentabilidade.id != null
                                    ? "msg-edit-sucesso-n"
                                    : "msg-save-sucesso-n",
                                { n: this.$t("rentabilidade") }
                            );
                        this.msgs.push(
                            new Message('success', msg, true, false)
                        );
                        this.redirectToList();
                    })
                    .catch((e) => {
                        let message = e.body.message;
                        if (message && this.$te(message)) {
                            if (message == 'msg-codigo-duplicado-n') {
                                this.msgs.push(
                                    new Message(
                                        'alert',
                                        this.$t(message, {
                                            n: rentabilidadeTemp.idrentabilidadeerp,
                                        }),
                                        true,
                                        false
                                    )
                                );
                            } else {
                                let msgErro = this.$t(e);
                                this.msgs.push(
                                    new Message('alert', msgErro, true, false)
                                );
                            }
                        } else {
                            this.msgs.push(
                                new Message(
                                    'error',
                                    'msg-problema-realizar-acao',
                                    true,
                                    false
                                )
                            );
                        }
                    })
                    .finally((_) => {
                        this.isLoading = false;
                        this.isSaving = false;
                        this.isChanged = true;
                    });
            } else {
                this.msgs.push(
                    new Message('alert', 'msg-campos-obrigatorios', true)
                );
                this.isLoading = false;
            }
        },
        promptChangeTab() {
            if (this.isChanged) {
                this.$root.confirm({
                    message: this.$t('msg-tem-certeza-alteracoes'),
                    onConfirm: (_) => {
                        this.isChanged = false;
                        this.redirectToList();
                    },
                });
            } else {
                this.redirectToList();
            }
        },
        redirectToList() {
            setTimeout(() => {
                this.$router.push({ name: 'ven-rentabilidade-list' });
            }, 1000);
        },
        colorpicked(cor) {
            this.rentabilidade.cor = cor;
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: auto;

    .page {
        position: relative;
    }
}
</style>
